import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import ModelsTable from './components/ModelsTable';
import ModelPage from './components/ModelPage';
import AuthPage from './components/AuthPage';
import OnboardingPage from './components/OnboardingPage';
import BookDiscoveryPage from './components/BookDiscoveryPage';
import ProfilePage from './components/ProfilePage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import AuthConfirmPage from './components/AuthConfirmPage';
import TickerPage from './components/TickerPage';
import ETFPage from './components/ETFPage';
import PowerListPage from './components/PowerListPage';
import FullPowerListPage from './components/FullPowerListPage';
import AnalyzePage from './components/AnalyzePage';
import WatchlistDetailPage from './components/WatchlistDetailPage';
import InsightsPage from './components/InsightsPage';
import MethodologyPage from './components/MethodologyPage';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import WhatIsTampPage from './components/WhatIsTampPage';
import TAMPComparisonPage from './components/TAMPComparisonPage';
import BlackRockComparisonPage from './components/BlackRockComparisonPage';
import RussellInvestmentsPage from './components/RussellInvestmentsPage';
import JPMorganAssetManagementPage from './components/JPMorganAssetManagementPage';
import ISharesModelPortfoliosPage from './components/ISharesModelPortfoliosPage';
import TeamPage from './components/TeamPage';
import LearnPage from './components/LearnPage';
import ContactPage from './components/ContactPage';
import ResearchPage from './components/ResearchPage';
import ChatPage from './components/ChatPage';
import PrivateRoute from './components/PrivateRoute';
import AdminPanel from './components/AdminPanel';

// Learn section pages
import MethodologyLearnPage from './components/pages/MethodologyPage';
import AlternativeDataPage from './components/pages/AlternativeDataPage';
import QuantFactorsPage from './components/pages/QuantFactorsPage';
import PortfolioConstructionPage from './components/pages/PortfolioConstructionPage';
import PlatformGuidePage from './components/pages/PlatformGuidePage';
import WorkflowIntegrationPage from './components/pages/WorkflowIntegrationPage';
import QuickReferencePage from './components/pages/QuickReferencePage';

// Paths that should NOT show the top nav sidebar
const NO_SIDEBAR_PATHS = [
  '/login', '/register', '/onboarding', '/book-discovery',
  '/forgot-password', '/reset-password', '/auth/confirm',
  '/',
  '/power-list', '/methodology', '/privacy', '/terms',
  '/what-is-tamp', '/tamp-comparison',
  '/blackrock-model-portfolios', '/blackrock',
  '/russell-investments', '/russell',
  '/jpmorgan-asset-management', '/jpmorgan',
  '/ishares-model-portfolios', '/ishares',
  '/team', '/contact',
];

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <MainLayout />
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

function MainLayout() {
  const location = useLocation();
  const showSidebar = !NO_SIDEBAR_PATHS.includes(location.pathname);

  return (
    <div className="flex flex-col h-screen bg-bg-100 overflow-x-hidden">
      {showSidebar && <Sidebar />}
      <div className="flex flex-col flex-1">
        <main className="flex-grow overflow-y-auto">
          <Routes>
            {/* ── Auth & onboarding ── */}
            <Route path="/" element={<AuthPage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/register" element={<AuthPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/confirm" element={<AuthConfirmPage />} />
            <Route path="/book-discovery" element={<BookDiscoveryPage />} />

            {/* Onboarding is private but doesn't require completed onboarding */}
            <Route path="/onboarding" element={
              <PrivateRoute skipOnboardingCheck>
                <OnboardingPage />
              </PrivateRoute>
            } />

            {/* Profile / settings */}
            <Route path="/profile" element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            } />

            {/* ── Public marketing pages ── */}
            <Route path="/power-list" element={<PowerListPage />} />
            <Route path="/methodology" element={<MethodologyPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/what-is-tamp" element={<WhatIsTampPage />} />
            <Route path="/tamp-comparison" element={<TAMPComparisonPage />} />
            <Route path="/blackrock-model-portfolios" element={<BlackRockComparisonPage />} />
            <Route path="/russell-investments" element={<RussellInvestmentsPage />} />
            <Route path="/jpmorgan-asset-management" element={<JPMorganAssetManagementPage />} />
            <Route path="/ishares-model-portfolios" element={<ISharesModelPortfoliosPage />} />
            <Route path="/blackrock" element={<BlackRockComparisonPage />} />
            <Route path="/russell" element={<RussellInvestmentsPage />} />
            <Route path="/jpmorgan" element={<JPMorganAssetManagementPage />} />
            <Route path="/ishares" element={<ISharesModelPortfoliosPage />} />

            {/* ── Protected app routes ── */}
            <Route path="/models" element={
              <PrivateRoute><ModelsTable /></PrivateRoute>
            } />
            <Route path="/model/:id" element={
              <PrivateRoute><ModelPage /></PrivateRoute>
            } />
            <Route path="/ticker/:cusip" element={
              <PrivateRoute><TickerPage /></PrivateRoute>
            } />
            <Route path="/etf/:ticker" element={
              <PrivateRoute><ETFPage /></PrivateRoute>
            } />
            <Route path="/rankings/power-list" element={
              <PrivateRoute><FullPowerListPage /></PrivateRoute>
            } />
            <Route path="/analyze" element={
              <PrivateRoute><AnalyzePage /></PrivateRoute>
            } />
            <Route path="/analyze/:watchlistId" element={
              <PrivateRoute><WatchlistDetailPage /></PrivateRoute>
            } />
            <Route path="/research" element={
              <PrivateRoute><ResearchPage /></PrivateRoute>
            } />
            <Route path="/chat" element={
              <PrivateRoute><ChatPage /></PrivateRoute>
            } />
            <Route path="/learn" element={
              <PrivateRoute><LearnPage /></PrivateRoute>
            } />
            <Route path="/learn/methodology" element={
              <PrivateRoute><MethodologyLearnPage /></PrivateRoute>
            } />
            <Route path="/learn/alternative-data" element={
              <PrivateRoute><AlternativeDataPage /></PrivateRoute>
            } />
            <Route path="/learn/quant-factors" element={
              <PrivateRoute><QuantFactorsPage /></PrivateRoute>
            } />
            <Route path="/learn/portfolio-construction" element={
              <PrivateRoute><PortfolioConstructionPage /></PrivateRoute>
            } />
            <Route path="/learn/platform-guide" element={
              <PrivateRoute><PlatformGuidePage /></PrivateRoute>
            } />
            <Route path="/learn/workflow-integration" element={
              <PrivateRoute><WorkflowIntegrationPage /></PrivateRoute>
            } />
            <Route path="/learn/quick-reference" element={
              <PrivateRoute><QuickReferencePage /></PrivateRoute>
            } />
            <Route path="/insights" element={
              <PrivateRoute><InsightsPage /></PrivateRoute>
            } />
            <Route path="/admin" element={
              <PrivateRoute><AdminPanel /></PrivateRoute>
            } />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default App;
